import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiErrorResult, ApiErrorResult_Constants, Types } from '@siza-staff-care/shared/data';
import { GenericDialogsService } from '@siza-staff-care/shared/generic-dialogs';
import {
	BaseError,
	BusinessError,
	DeleteReferencedDataError,
	ObjectNotFoundError,
	ServerErrorShownError,
} from '@siza-staff-care/shared/global-errors';
import { EMPTY, Observable, throwError, timer } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { NetworkStatsStore } from './network-stats.store';
import { RetryInterceptorStore } from './retry-interceptor-store';

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
	constructor(
		private genericDialogs: GenericDialogsService,
		private store: RetryInterceptorStore,
		private statsStore: NetworkStatsStore,
		private snackBar: MatSnackBar,
	) {}

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		this.statsStore.bumpRequests();

		return next.handle(request).pipe(
			tap((response: { type: number }) => {
				if (response.type !== 0) {
					this.store.networkOk();
				}
			}),

			retry({
				delay: (error: HttpErrorResponse) => {
					if (error.status === 0) {
						this.store.networkError();

						return timer(1000);
					}

					return throwError(() => error);
				},
			}),

			catchError((err) => {
				// If we caught an error, we're not retrying, so the network is OK (available/online).
				this.store.networkOk();

				if (err instanceof HttpErrorResponse) {
					switch (err.status) {
						case 422:
							// If it's a 422, unwrap the error messages and show a dialog.
							if (err.error?.$type === Types.ApiErrorResult) {
								const error = this.handleUnprocessableEntityError(err.error as ApiErrorResult);
								if (error) {
									return throwError(() => error);
								}
							}
							break;
						case 500:
							this.genericDialogs.showServerError(err);

							// We've shown the error dialog - throw a special error so that the calling code goes no further.
							throw new ServerErrorShownError(err.error);
						case 503:
						case 504:
							this.statsStore.bumpErrors();

							this.snackBar.open('The server is unavailable', '', {
								duration: 2000,
							});

							return EMPTY;
					}
				}

				// Give up - rethrow the error
				return throwError(() => err);
			}),
		);
	}

	private handleUnprocessableEntityError(error: ApiErrorResult): BaseError | undefined {
		switch (error.errorType) {
			case ApiErrorResult_Constants.ErrorType_GenericBusiness:
				return new BusinessError(error);
			case ApiErrorResult_Constants.ErrorType_DeleteReferenced:
				return new DeleteReferencedDataError(error);
			case ApiErrorResult_Constants.ErrorType_ObjectNotFound:
				return new ObjectNotFoundError(error);
			case ApiErrorResult_Constants.ErrorType_ConfirmableWarning:
				// TODO: Handle confirmable warning
				break;
		}

		return;
	}
}
